import React, { createContext, useContext, useState, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import AlertDialog from '../components/AlertDialog';
import ConfirmDialog from '../components/ConfirmDialog';

interface DialogContextType {
  alert: (message: string, title?: string, type?: 'success' | 'danger' | 'warning' | 'info') => Promise<void>;
  confirm: (message: string, title?: string, type?: 'warning' | 'danger') => Promise<boolean>;
}

const DialogContext = createContext<DialogContextType | null>(null);

export const DialogProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { t } = useTranslation('common');
  const [alertState, setAlertState] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'success' | 'danger' | 'warning' | 'info';
    resolve?: () => void;
  }>({ isOpen: false, title: '', message: '', type: 'info' });

  const [confirmState, setConfirmState] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'warning' | 'danger';
    resolve?: (value: boolean) => void;
  }>({ isOpen: false, title: '', message: '', type: 'warning' });

  const alert = (message: string, title = t('confirm'), type: 'success' | 'danger' | 'warning' | 'info' = 'info'): Promise<void> => {
    return new Promise((resolve) => {
      setAlertState({ isOpen: true, title, message, type, resolve });
    });
  };

  const confirm = (message: string, title = t('confirm'), type: 'warning' | 'danger' = 'warning'): Promise<boolean> => {
    return new Promise((resolve) => {
      setConfirmState({ isOpen: true, title, message, type, resolve });
    });
  };

  const handleAlertClose = () => {
    alertState.resolve?.();
    setAlertState({ isOpen: false, title: '', message: '', type: 'info' });
  };

  const handleConfirmClose = (result: boolean) => {
    confirmState.resolve?.(result);
    setConfirmState({ isOpen: false, title: '', message: '', type: 'warning' });
  };

  return (
    <DialogContext.Provider value={{ alert, confirm }}>
      {children}

      <AlertDialog
        isOpen={alertState.isOpen}
        title={alertState.title}
        message={alertState.message}
        type={alertState.type}
        confirmText={t('confirm')}
        onConfirm={handleAlertClose}
      />

      <ConfirmDialog
        isOpen={confirmState.isOpen}
        title={confirmState.title}
        message={confirmState.message}
        type={confirmState.type}
        confirmText={t('confirm')}
        cancelText={t('cancel')}
        onConfirm={() => handleConfirmClose(true)}
        onCancel={() => handleConfirmClose(false)}
      />
    </DialogContext.Provider>
  );
};

export const useDialog = (): DialogContextType => {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error('useDialog must be used within a DialogProvider');
  }
  return context;
};